import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Users, Gift, BarChart3, ClipboardList, Download, RefreshCw, Search, ToggleLeft, ToggleRight, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 bg-[#111009] border-[#d4af37]/20">
        <div className="text-center mb-8">
          <Lock className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-white/50 text-sm mt-2">Enter your password to continue</p>
        </div>

        <form onSubmit={handleLogin}>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-[#0a0908] border-white/10 text-white mb-4"
            data-testid="admin-password-input"
          />
          
          {error && (
            <p className="text-red-400 text-sm mb-4">{error}</p>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
            data-testid="admin-login-btn"
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </form>
      </Card>
    </div>
  );
};

// Waitlist Tab Component
const WaitlistTab = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const fetchWaitlist = async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist`, { withCredentials: true });
      setWaitlist(response.data.submissions);
      setStats(response.data.stats);
    } catch (err) {
      toast.error("Failed to load waitlist");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/waitlist/${id}/status`, { status }, { withCredentials: true });
      toast.success("Status updated");
      fetchWaitlist();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="space-y-6">
      {/* Detail Modal */}
      {selectedEntry && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setSelectedEntry(null)}
        >
          <div 
            className="relative max-w-2xl w-full p-6 rounded-2xl max-h-[80vh] overflow-y-auto"
            style={{ background: '#111009', border: '1px solid rgba(212,175,55,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedEntry(null)}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold text-white mb-6">Application Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Name</label>
                <p className="text-white text-lg">{selectedEntry.first_name}</p>
              </div>
              
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Email</label>
                <p className="text-[#d4af37]">
                  <a href={`mailto:${selectedEntry.email}`}>{selectedEntry.email}</a>
                </p>
              </div>
              
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Journey & Why They Want to Join</label>
                <p className="text-white/80 mt-1 whitespace-pre-wrap leading-relaxed">
                  {selectedEntry.idea_description || "Not provided"}
                </p>
              </div>
              
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Submitted</label>
                <p className="text-white/70">{new Date(selectedEntry.submitted_at).toLocaleString()}</p>
              </div>
              
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Status</label>
                <select
                  value={selectedEntry.status}
                  onChange={(e) => {
                    updateStatus(selectedEntry.id, e.target.value);
                    setSelectedEntry({...selectedEntry, status: e.target.value});
                  }}
                  className="mt-1 bg-[#0a0908] border border-white/10 rounded px-3 py-2 text-white w-full"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase tracking-wider">Total</p>
          <p className="text-3xl font-bold text-white">{stats.total || 0}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase tracking-wider">Pending</p>
          <p className="text-3xl font-bold text-yellow-400">{stats.pending || 0}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase tracking-wider">Approved</p>
          <p className="text-3xl font-bold text-green-400">{stats.approved || 0}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase tracking-wider">Rejected</p>
          <p className="text-3xl font-bold text-red-400">{stats.rejected || 0}</p>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-[#111009] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0a0908]">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Idea</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-white/50">Loading...</td>
                </tr>
              ) : waitlist.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-white/50">No submissions yet</td>
                </tr>
              ) : (
                waitlist.map((entry) => (
                  <tr 
                    key={entry.id} 
                    className="hover:bg-white/5 cursor-pointer"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <td className="px-4 py-3 text-white">{entry.first_name}</td>
                    <td className="px-4 py-3 text-white/70">{entry.email}</td>
                    <td className="px-4 py-3 text-white/50 max-w-xs truncate">{entry.idea_description || "-"}</td>
                    <td className="px-4 py-3 text-white/50 text-sm">{new Date(entry.submitted_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={entry.status}
                        onChange={(e) => updateStatus(entry.id, e.target.value)}
                        className="bg-[#0a0908] border border-white/10 rounded px-2 py-1 text-sm text-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Users Tab Component
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState("signup_date");
  const [sortDir, setSortDir] = useState("desc");

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data.users);
      setFilteredUsers(response.data.users);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = users.filter(u => 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const exportCSV = () => {
    window.open(`${API}/admin/users/export`, "_blank");
  };

  return (
    <div className="space-y-6">
      {/* Search and Export */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 bg-[#0a0908] border-white/10 text-white"
          />
        </div>
        <Button onClick={exportCSV} variant="outline" className="border-[#d4af37]/30 text-[#d4af37]">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-[#111009] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0908]">
              <tr>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase cursor-pointer" onClick={() => handleSort("name")}>Name</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase cursor-pointer" onClick={() => handleSort("email")}>Email</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase cursor-pointer" onClick={() => handleSort("signup_date")}>Signup</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase">Auth</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase cursor-pointer" onClick={() => handleSort("account_type")}>Type</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase cursor-pointer" onClick={() => handleSort("current_day")}>Day</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase">Idea Selected</th>
                <th className="px-3 py-3 text-left text-xs text-white/50 uppercase">Decision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-white/50">Loading...</td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-white/50">No users found</td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-white/5">
                    <td className="px-3 py-2 text-white">{user.name || "-"}</td>
                    <td className="px-3 py-2 text-white/70 text-xs">{user.email}</td>
                    <td className="px-3 py-2 text-white/50 text-xs">{user.signup_date ? new Date(user.signup_date).toLocaleDateString() : "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${user.auth_provider === "google" ? "bg-blue-500/20 text-blue-400" : "bg-gray-500/20 text-gray-400"}`}>
                        {user.auth_provider}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${user.account_type === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                        {user.account_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-white">{user.current_day}/7</td>
                    <td className="px-3 py-2 text-white/50 text-xs max-w-[150px] truncate">{user.day3_selected_idea || "-"}</td>
                    <td className="px-3 py-2">
                      {user.day7_decision && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          user.day7_decision === "GO" ? "bg-green-500/20 text-green-400" :
                          user.day7_decision === "PIVOT" ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-red-500/20 text-red-400"
                        }`}>
                          {user.day7_decision}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Promo Codes Tab Component
const PromoCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newCode, setNewCode] = useState({
    code: "",
    discount_type: "percentage",
    discount_amount: 0,
    max_uses: "",
    expiry_date: "",
    active: true
  });

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setCodes(response.data.promo_codes);
    } catch (err) {
      toast.error("Failed to load promo codes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const createCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        ...newCode,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expiry_date: newCode.expiry_date || null
      }, { withCredentials: true });
      toast.success("Promo code created");
      setNewCode({ code: "", discount_type: "percentage", discount_amount: 0, max_uses: "", expiry_date: "", active: true });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create promo code");
    }
  };

  const deactivateCode = async (id) => {
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { withCredentials: true });
      toast.success("Promo code deactivated");
      fetchCodes();
    } catch (err) {
      toast.error("Failed to deactivate");
    }
  };

  return (
    <div className="space-y-6">
      {/* Create Form */}
      <Card className="p-6 bg-[#111009] border-white/10">
        <h3 className="text-white font-semibold mb-4">Create New Promo Code</h3>
        <form onSubmit={createCode} className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Input
            value={newCode.code}
            onChange={(e) => setNewCode({...newCode, code: e.target.value})}
            placeholder="CODE"
            className="bg-[#0a0908] border-white/10 text-white uppercase"
            required
          />
          <select
            value={newCode.discount_type}
            onChange={(e) => setNewCode({...newCode, discount_type: e.target.value})}
            className="bg-[#0a0908] border border-white/10 rounded px-3 text-white"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed ($)</option>
          </select>
          <Input
            type="number"
            value={newCode.discount_amount}
            onChange={(e) => setNewCode({...newCode, discount_amount: parseFloat(e.target.value)})}
            placeholder="Amount"
            className="bg-[#0a0908] border-white/10 text-white"
            required
          />
          <Input
            type="number"
            value={newCode.max_uses}
            onChange={(e) => setNewCode({...newCode, max_uses: e.target.value})}
            placeholder="Max uses (blank=unlimited)"
            className="bg-[#0a0908] border-white/10 text-white"
          />
          <Input
            type="date"
            value={newCode.expiry_date}
            onChange={(e) => setNewCode({...newCode, expiry_date: e.target.value})}
            className="bg-[#0a0908] border-white/10 text-white"
          />
          <Button type="submit" className="bg-[#d4af37] text-[#0a0908]">Create</Button>
        </form>
      </Card>

      {/* Table */}
      <Card className="bg-[#111009] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0a0908]">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Used</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Max</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs text-white/50 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-white/50">Loading...</td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-white/50">No promo codes yet</td>
                </tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.id} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-white font-mono">{code.code}</td>
                    <td className="px-4 py-3 text-white">
                      {code.discount_type === "percentage" ? `${code.discount_amount}%` : `$${code.discount_amount}`}
                    </td>
                    <td className="px-4 py-3 text-white/70">{code.times_used || 0}</td>
                    <td className="px-4 py-3 text-white/50">{code.max_uses || "∞"}</td>
                    <td className="px-4 py-3 text-white/50">{code.expiry_date || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${code.active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                        {code.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {code.active && (
                        <Button 
                          onClick={() => deactivateCode(code.id)} 
                          variant="ghost" 
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          Deactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// Metrics Tab Component
const MetricsTab = () => {
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/metrics`, { withCredentials: true });
      setMetrics(response.data);
    } catch (err) {
      toast.error("Failed to load metrics");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  if (isLoading) {
    return <div className="text-center text-white/50 py-12">Loading metrics...</div>;
  }

  if (!metrics) {
    return <div className="text-center text-white/50 py-12">Failed to load metrics</div>;
  }

  const { summary, day_completions, ai_intelligence } = metrics;

  return (
    <div className="space-y-6">
      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button onClick={fetchMetrics} variant="outline" className="border-[#d4af37]/30 text-[#d4af37]">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Total Users</p>
          <p className="text-2xl font-bold text-white">{summary.total_users}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Paid Users</p>
          <p className="text-2xl font-bold text-green-400">{summary.paid_users}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Conversion</p>
          <p className="text-2xl font-bold text-[#d4af37]">{summary.conversion_rate}%</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Total Revenue</p>
          <p className="text-2xl font-bold text-white">${summary.total_revenue}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">This Week</p>
          <p className="text-2xl font-bold text-white">${summary.week_revenue}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Promo Uses</p>
          <p className="text-2xl font-bold text-white">{summary.active_promo_uses}</p>
        </Card>
        <Card className="p-4 bg-[#111009] border-white/10">
          <p className="text-white/50 text-xs uppercase">Avg Day</p>
          <p className="text-2xl font-bold text-white">{summary.avg_day_reached}</p>
        </Card>
      </div>

      {/* Day Completion Funnel */}
      <Card className="p-6 bg-[#111009] border-white/10">
        <h3 className="text-white font-semibold mb-4">Day-by-Day Completion Funnel</h3>
        <div className="flex items-end gap-4 h-48">
          {Object.entries(day_completions).map(([day, count]) => {
            const maxCount = Math.max(...Object.values(day_completions), 1);
            const height = (count / maxCount) * 100;
            return (
              <div key={day} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-[#d4af37] rounded-t"
                  style={{ height: `${height}%`, minHeight: count > 0 ? "8px" : "0" }}
                />
                <p className="text-white/50 text-xs mt-2">Day {day}</p>
                <p className="text-white text-sm font-bold">{count}</p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* AI Intelligence */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 bg-[#111009] border-white/10">
          <h3 className="text-white font-semibold mb-4">Idea Categories (Day 3)</h3>
          <div className="space-y-3">
            {Object.entries(ai_intelligence.idea_categories || {}).map(([category, count]) => (
              <div key={category} className="flex justify-between items-center">
                <span className="text-white/70">{category}</span>
                <span className="text-[#d4af37] font-bold">{count}</span>
              </div>
            ))}
            {Object.keys(ai_intelligence.idea_categories || {}).length === 0 && (
              <p className="text-white/50">No ideas generated yet</p>
            )}
          </div>
        </Card>

        <Card className="p-6 bg-[#111009] border-white/10">
          <h3 className="text-white font-semibold mb-4">Sample Pain Points (Day 1)</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(ai_intelligence.pain_points || []).slice(0, 10).map((pain, i) => (
              <p key={i} className="text-white/60 text-sm border-l-2 border-[#d4af37]/30 pl-3">
                {pain}
              </p>
            ))}
            {(ai_intelligence.pain_points || []).length === 0 && (
              <p className="text-white/50">No pain points collected yet</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [betaMode, setBetaMode] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchBetaMode();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/admin/verify`, { withCredentials: true });
      setIsAuthenticated(response.data.authenticated);
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setIsChecking(false);
    }
  };

  const fetchBetaMode = async () => {
    try {
      const response = await axios.get(`${API}/admin/beta-mode`);
      setBetaMode(response.data.enabled);
    } catch (err) {
      console.error("Failed to fetch beta mode");
    }
  };

  const toggleBetaMode = async () => {
    try {
      const response = await axios.post(`${API}/admin/beta-mode`, { enabled: !betaMode }, { withCredentials: true });
      setBetaMode(response.data.enabled);
      toast.success(`Beta mode ${response.data.enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error("Failed to toggle beta mode");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
    } catch (err) {
      console.error("Logout error");
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
        <p className="text-white/50">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0908] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">
              <span className="text-[#d4af37]">Prayer to Prototype</span>
              <span className="text-white/50 ml-2">Admin</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Beta Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/50">Beta Mode</span>
              <button
                onClick={toggleBetaMode}
                className={`p-1 rounded ${betaMode ? "text-green-400" : "text-white/30"}`}
                data-testid="beta-mode-toggle"
              >
                {betaMode ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
              </button>
            </div>
            
            <Button onClick={handleLogout} variant="ghost" className="text-white/50 hover:text-white">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="waitlist" className="space-y-6">
          <TabsList className="bg-[#111009] border border-white/10">
            <TabsTrigger value="waitlist" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0a0908]">
              <ClipboardList className="w-4 h-4 mr-2" /> Waitlist
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0a0908]">
              <Users className="w-4 h-4 mr-2" /> All Users
            </TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0a0908]">
              <Gift className="w-4 h-4 mr-2" /> Promo Codes
            </TabsTrigger>
            <TabsTrigger value="metrics" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0a0908]">
              <BarChart3 className="w-4 h-4 mr-2" /> Metrics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="waitlist">
            <WaitlistTab />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="promo">
            <PromoCodesTab />
          </TabsContent>
          <TabsContent value="metrics">
            <MetricsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
